import React, { useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => {
    const directToken = (params.get("token") || "").trim();
    if (directToken) {
      return decodeURIComponent(directToken);
    }

    // Fallback for malformed links where query may contain token%3D... or token=... in the key segment.
    for (const [key, value] of params.entries()) {
      if (!value && key.toLowerCase().startsWith("token=")) {
        return decodeURIComponent(key.slice(6).trim());
      }
    }

    const rawSearch = typeof window !== "undefined" ? window.location.search || "" : "";
    const decoded = decodeURIComponent(rawSearch.replace(/^\?/, ""));
    const match = decoded.match(/(?:^|[?&])token=([^&]+)/i) || decoded.match(/token=([^&]+)/i);
    if (match?.[1]) {
      return match[1].trim();
    }

    return "";
  }, [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing token");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/set-password`,
        { token, password },
        { withCredentials: true },
      );

      toast.success("Password updated successfully.");

      try {
        const me = await axios.get(`${API}/me`, { withCredentials: true });
        if (me.data?.role === "admin") {
          nav("/admin/users", { replace: true });
          return;
        }
        if (me.data?.hasAccess) {
          nav("/app", { replace: true });
          return;
        }
        nav("/billing", { replace: true });
        return;
      } catch {
        const role = response?.data?.role;
        const hasAccess = response?.data?.hasAccess;
        if (role === "admin") {
          nav("/admin/users", { replace: true });
          return;
        }
        if (hasAccess) {
          nav("/app", { replace: true });
          return;
        }
        nav("/billing", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md" data-testid="set-password-card">
        <CardHeader>
          <CardTitle className="text-lg">Set password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="set-password-form">
            <div className="space-y-2">
              <label htmlFor="new-password" className="text-sm text-slate-600">New password</label>
              <Input
                id="new-password"
                data-testid="set-password-new-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="confirm-password" className="text-sm text-slate-600">Confirm password</label>
              <Input
                id="confirm-password"
                data-testid="set-password-confirm-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                type="password"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="set-password-submit-button"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={loading}
            >
              {loading ? "Saving..." : "Set password"}
            </Button>
            <div className="text-sm text-slate-600">
              <Link to="/login" className="underline" data-testid="set-password-login-link">
                Back to login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
